import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  MessageSquare, 
  Star, 
  Trash2, 
  CheckCircle, 
  XCircle,
  Mail,
  Phone,
  Calendar,
  User,
  LogOut
} from 'lucide-react';
import { toast } from 'sonner';

const Admin = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [loadingReviews, setLoadingReviews] = useState(true);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    toast.success('Uğurla çıxış edildi');
    navigate('/admin/login');
  };

  useEffect(() => {
    fetchContacts();
    fetchReviews();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoadingContacts(true);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contacts`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      
      if (data.success) {
        setContacts(data.contacts);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Əlaqə mesajları yüklənərkən xəta baş verdi');
    } finally {
      setLoadingContacts(false);
    }
  };

  const fetchReviews = async () => {
    try {
      setLoadingReviews(true);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/reviews/all`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      
      if (data.success) {
        setReviews(data.reviews);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Rəylər yüklənərkən xəta baş verdi');
    } finally {
      setLoadingReviews(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Bu rəyi silmək istədiyinizə əminsiniz?')) {
      return;
    }

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/reviews/${reviewId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Rəy silindi');
        fetchReviews();
      } else {
        toast.error('Rəy silinərkən xəta baş verdi');
      }
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error('Rəy silinərkən xəta baş verdi');
    }
  };

  const handleToggleApproval = async (reviewId, currentStatus) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/reviews/${reviewId}/approve`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ approved: !currentStatus }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(currentStatus ? 'Rəy gizlədildi' : 'Rəy təsdiqləndi');
        fetchReviews();
      } else {
        toast.error('Əməliyyat zamanı xəta baş verdi');
      }
    } catch (error) {
      console.error('Error toggling approval:', error);
      toast.error('Əməliyyat zamanı xəta baş verdi');
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('az-AZ', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold mb-2">Admin Panel</h1>
            <p className="text-gray-600">Rəyləri və əlaqə mesajlarını idarə edin</p>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="flex items-center gap-2 border-red-600 text-red-600 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4" />
            Çıxış
          </Button>
        </div>

        <Tabs defaultValue="reviews" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-8">
            <TabsTrigger value="reviews" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Rəylər ({reviews.length})
            </TabsTrigger>
            <TabsTrigger value="contacts" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Mesajlar ({contacts.length})
            </TabsTrigger>
          </TabsList>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            {loadingReviews ? (
              <div className="text-center py-12">
                <p className="text-gray-600">Yüklənir...</p>
              </div>
            ) : reviews.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">Hələ rəy yoxdur</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {reviews.map((review) => (
                  <Card key={review.id} className="overflow-hidden">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="bg-red-100 text-red-600 w-10 h-10 rounded-full flex items-center justify-center font-semibold">
                              {review.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <h3 className="font-semibold text-lg">{review.name}</h3>
                              <div className="flex items-center gap-2">
                                {renderStars(review.rating)}
                                <span className="text-sm text-gray-500">
                                  {formatDate(review.date)}
                                </span>
                              </div>
                            </div>
                          </div>
                          <p className="text-gray-700 leading-relaxed mb-3">{review.comment}</p>
                          <div className="flex items-center gap-2">
                            {review.approved ? (
                              <Badge variant="success" className="bg-green-100 text-green-700">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Təsdiqlənib
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="bg-gray-100 text-gray-700">
                                <XCircle className="w-3 h-3 mr-1" />
                                Gözləmədə
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <Button
                            size="sm"
                            variant={review.approved ? "outline" : "default"}
                            onClick={() => handleToggleApproval(review.id, review.approved)}
                            className={review.approved ? "" : "bg-green-600 hover:bg-green-700"}
                          >
                            {review.approved ? (
                              <>
                                <XCircle className="w-4 h-4 mr-1" />
                                Gizlət
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Təsdiqlə
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteReview(review.id)}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Sil
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            {loadingContacts ? (
              <div className="text-center py-12">
                <p className="text-gray-600">Yüklənir...</p>
              </div>
            ) : contacts.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">Hələ mesaj yoxdur</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {contacts.map((contact, index) => (
                  <Card key={index} className="overflow-hidden">
                    <CardHeader className="bg-gray-50 border-b">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg mb-2">{contact.subject}</CardTitle>
                          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              {contact.name}
                            </div>
                            <div className="flex items-center gap-1">
                              <Mail className="w-4 h-4" />
                              <a href={`mailto:${contact.email}`} className="hover:text-red-600">
                                {contact.email}
                              </a>
                            </div>
                            <div className="flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              <a href={`tel:${contact.phone}`} className="hover:text-red-600">
                                {contact.phone}
                              </a>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Calendar className="w-4 h-4" />
                          {formatDate(contact.created_at)}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-6">
                      <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {contact.message}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
